import React, { useCallback, useEffect, useState } from "react";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    FormControlLabel,
    LinearProgress,
    Switch,
    Table,
    TextField,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import moment from "moment";
import AdminNotificationService from "./AdminNotificationService";

const OVERVIEW_APP_TAB_KEY = "admin.notification.overview.app";

const KIND_LABELS = {
    ADMIN: "Admin",
    PUBLISH: "Puzzle published",
    TEST: "Test",
    DAILY_GIFT: "Daily gift",
};

function kindChip(kind) {
    const label = KIND_LABELS[kind] || kind || "—";
    let color = "default";
    if (kind === "ADMIN") color = "primary";
    if (kind === "PUBLISH") color = "success";
    if (kind === "TEST") color = "default";
    return <Chip size="small" label={label} color={color} variant="outlined" />;
}

function formatInterval(ms) {
    if (!Number.isFinite(ms) || ms <= 0) {
        return null;
    }
    const duration = moment.duration(ms);
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    if (days >= 1) {
        return hours > 0 ? `${days}d ${hours}h` : `${days} day${days === 1 ? "" : "s"}`;
    }
    const wholeHours = Math.floor(duration.asHours());
    if (wholeHours >= 1) {
        return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours} hour${wholeHours === 1 ? "" : "s"}`;
    }
    const mins = Math.max(1, Math.round(duration.asMinutes()));
    return `${mins} minute${mins === 1 ? "" : "s"}`;
}

function intervalFromRows(rows) {
    const times = (Array.isArray(rows) ? rows : [])
        .map((row) => Number(row?.createdAt))
        .filter((value) => Number.isFinite(value) && value > 0)
        .sort((a, b) => a - b);
    if (times.length < 2) {
        return null;
    }
    let total = 0;
    for (let i = 1; i < times.length; i += 1) {
        total += times[i] - times[i - 1];
    }
    return {
        ms: total / (times.length - 1),
        samples: times.length,
    };
}

function formatWhen(value) {
    if (value == null) return "—";
    const m = moment(value);
    if (!m.isValid()) return "—";
    return `${m.format("YYYY-MM-DD HH:mm")} (${m.locale("en").fromNow()})`;
}

function formatUserResult(row) {
    const sent = Number(row?.sentUserCount ?? row?.sentCount) || 0;
    const recipients = Number(row?.recipientUserCount ?? row?.recipientCount) || 0;
    if (recipients <= 0) {
        return `${sent} user${sent === 1 ? "" : "s"}`;
    }
    return `${sent} of ${recipients} user${recipients === 1 ? "" : "s"}`;
}

function formatDeviceResult(row) {
    const sent = Number(row?.sentCount) || 0;
    const recipients = Number(row?.recipientCount) || 0;
    const sns = Number(row?.snsEndpointCount) || 0;
    const enabled = Number(row?.snsEnabledEndpointCount);
    if (recipients <= 0 && sns <= 0) {
        return "";
    }
    let text = `${sent} of ${recipients} device${recipients === 1 ? "" : "s"}`;
    if (sns > 0) {
        text += ` · ${sns} SNS endpoint${sns === 1 ? "" : "s"}`;
        if (Number.isFinite(enabled) && enabled >= 0) {
            text += ` (${enabled} enabled)`;
        }
    }
    return text;
}

function SendSummary({ label, send }) {
    if (!send) {
        return (
            <Typography variant="body2" color="text.secondary">
                {label}: none yet with sent &gt; 0
            </Typography>
        );
    }
    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {label}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                {kindChip(send.kind)}
                <Typography variant="body2">{formatWhen(send.createdAt)}</Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                {send.title || "—"}
            </Typography>
            {send.body && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {send.body}
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {formatUserResult(send)}
            </Typography>
            {formatDeviceResult(send) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {formatDeviceResult(send)}
                </Typography>
            )}
        </Box>
    );
}

function DailyGiftCard({ gift, onEnabledChange, onTestOnlyChange, onSendTest }) {
    const enabled = gift?.enabled !== false;
    const testOnly = gift?.testOnly === true;
    const examples = Array.isArray(gift?.examples) ? gift.examples : [];
    const [sending, setSending] = useState(false);
    const [sendNote, setSendNote] = useState("");
    return (
        <Card sx={{ height: "100%" }}>
            <CardContent>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                    <Typography variant="h6">Daily gift</Typography>
                    <FormControlLabel
                        sx={{ m: 0 }}
                        label="Send"
                        control={
                            <Switch
                                checked={enabled}
                                onChange={(event) => onEnabledChange(event.target.checked)}
                                slotProps={{ input: { "aria-label": "Send daily gift reminder" } }}
                            />
                        }
                    />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {testOnly
                        ? "The reminder goes only to test accounts, around 6:00 pm every day."
                        : "One reminder every day, around 6:00 pm. A different message is chosen each day."}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                    <FormControlLabel
                        sx={{ m: 0 }}
                        label="Test accounts only"
                        control={
                            <Switch
                                checked={testOnly}
                                onChange={(event) => onTestOnlyChange(event.target.checked)}
                                slotProps={{ input: { "aria-label": "Send daily gift to test accounts only" } }}
                            />
                        }
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={sending}
                        onClick={async () => {
                            setSending(true);
                            setSendNote("");
                            try {
                                const note = await onSendTest();
                                if (note) {
                                    setSendNote(note);
                                }
                            } finally {
                                setSending(false);
                            }
                        }}
                    >
                        {sending ? "Sending…" : "Send to test accounts"}
                    </Button>
                </Box>
                {sendNote && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {sendNote}
                    </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                    {enabled
                        ? (gift?.sendAtLabel ? `Next send: ${gift.sendAtLabel}` : "Next send is chosen for the next day.")
                        : "Sending is off."}
                </Typography>
                <SendSummary label="Last daily gift" send={gift?.lastSend} />
                {examples.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Messages
                        </Typography>
                        {examples.map((example) => (
                            <Typography key={example.title} variant="body2" sx={{ mb: 0.25 }}>
                                {example.title} — {example.body}
                            </Typography>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

function AppBatchCard({ app, onSendEnabledChange, onBatchSizeChange, onMemoChange }) {
    const pending = Number(app?.pendingCount) || 0;
    const remaining = Number(app?.remainingToBatch) || 0;
    const publishedSince = app?.publishedSinceLastSend != null ? app.publishedSinceLastSend : pending;
    const size = Number(app?.batchSize) > 0 ? Number(app.batchSize) : 15;
    const [limitDraft, setLimitDraft] = useState(String(size));
    const [savingLimit, setSavingLimit] = useState(false);
    const savedMemo = app?.memo == null ? "" : String(app.memo);
    const [memoDraft, setMemoDraft] = useState(savedMemo);
    const [savingMemo, setSavingMemo] = useState(false);

    useEffect(() => {
        setLimitDraft(String(size));
    }, [size]);

    useEffect(() => {
        setMemoDraft(savedMemo);
    }, [savedMemo]);

    const commitLimit = async () => {
        const next = Number.parseInt(String(limitDraft).trim(), 10);
        if (!Number.isFinite(next) || next < 1 || next > 1000 || next === size) {
            setLimitDraft(String(size));
            return;
        }
        setSavingLimit(true);
        const saved = await onBatchSizeChange(app?.appCode, next);
        setSavingLimit(false);
        if (!saved) {
            setLimitDraft(String(size));
        }
    };

    const commitMemo = async () => {
        if (memoDraft === savedMemo) {
            return;
        }
        if (memoDraft.length > 2000) {
            setMemoDraft(savedMemo);
            return;
        }
        setSavingMemo(true);
        const saved = await onMemoChange(app?.appCode, memoDraft);
        setSavingMemo(false);
        if (!saved) {
            setMemoDraft(savedMemo);
        }
    };
    const progress = Math.min(100, Math.round((pending / size) * 100));
    const ready = pending >= size;
    const noDevices = (app?.notifiableTokenCount === 0) && !(Number(app?.snsEnabledEndpointCount) > 0);
    const sendEnabled = app?.sendEnabled !== false;
    const targetUsers = Number(app?.targetUserCount) || 0;

    return (
        <Card sx={{ height: "100%" }}>
            <CardContent>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                    <Box>
                        <Typography variant="h6">New puzzles</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Sends when new puzzles arrive.
                        </Typography>
                    </Box>
                    <FormControlLabel
                        sx={{ m: 0 }}
                        label="Send"
                        control={
                            <Switch
                                checked={sendEnabled}
                                onChange={(event) => onSendEnabledChange(app?.appCode, event.target.checked)}
                                slotProps={{ input: { "aria-label": `Send ${app?.appName || app?.appCode}` } }}
                            />
                        }
                    />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, color: "secondary.main", lineHeight: 1 }}>
                    {targetUsers}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    target users
                </Typography>
                {!sendEnabled ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Sending is off. Counting and the progress bar still run.
                    </Typography>
                ) : null}
                <SendSummary label="Last send" send={app?.lastSend} />
                {noDevices ? (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                        No devices registered — publish notifications cannot send until a player opts in.
                    </Alert>
                ) : Number(app?.snsEndpointCount) > 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        AWS SNS: {Number(app.snsEnabledEndpointCount) || 0} enabled of {app.snsEndpointCount} endpoints
                    </Typography>
                ) : null}
                <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Since last publish notification
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {publishedSince}{" "}
                        <Typography component="span" variant="body2" color="text.secondary">
                            puzzle{publishedSince === 1 ? "" : "s"} published
                        </Typography>
                    </Typography>
                </Box>
                <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            Batch counter
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {pending} /
                            </Typography>
                            <TextField
                                size="small"
                                type="number"
                                value={limitDraft}
                                disabled={savingLimit}
                                onChange={(event) => setLimitDraft(event.target.value)}
                                onBlur={commitLimit}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.currentTarget.blur();
                                    }
                                }}
                                slotProps={{
                                    htmlInput: {
                                        min: 1,
                                        max: 1000,
                                        "aria-label": `Batch limit for ${app?.appName || app?.appCode}`,
                                    },
                                }}
                                sx={{ width: 88 }}
                            />
                        </Box>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, progress)}
                        color={ready ? "warning" : "primary"}
                        sx={{ height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {ready
                            ? "Ready to send — counter resets to 0 after the notification goes out"
                            : `${remaining} more to trigger a send`}
                    </Typography>
                </Box>
                <TextField
                    label="Memo"
                    placeholder="Notes for this app"
                    multiline
                    minRows={3}
                    fullWidth
                    value={memoDraft}
                    disabled={savingMemo}
                    onChange={(event) => setMemoDraft(event.target.value.slice(0, 2000))}
                    onBlur={commitMemo}
                    slotProps={{
                        htmlInput: {
                            maxLength: 2000,
                            "aria-label": `Memo for ${app?.appName || app?.appCode}`,
                        },
                    }}
                    sx={{ mt: 2 }}
                />
                {app?.nextGuess && (
                    <Alert severity={noDevices ? "error" : ready ? "warning" : "info"} sx={{ mt: 2 }}>
                        {app.nextGuess}
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
}

function RecentSendsCard({ app, rows, loading }) {
    const name = app?.appName || app?.appCode || "this app";
    const serverIntervalMs = Number(app?.averageIntervalMs);
    const serverSamples = Number(app?.averageIntervalSamples);
    const fallbackInterval = intervalFromRows(rows);
    const intervalMs = Number.isFinite(serverIntervalMs) && serverIntervalMs > 0
        ? serverIntervalMs
        : fallbackInterval?.ms;
    const intervalSamples = Number.isFinite(serverSamples) && serverSamples >= 2
        ? serverSamples
        : (fallbackInterval?.samples || 0);
    const intervalLabel = formatInterval(intervalMs);
    return (
        <Card sx={{ height: "100%" }}>
            <CardContent>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }}>
                    <Box>
                        <Typography variant="h6">
                            Recent sends
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Latest {name} notifications with at least one user reached (sent &gt; 0).
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                            Average send interval
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {intervalLabel || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {intervalSamples >= 2
                                ? `From the last ${intervalSamples} sends`
                                : "Need at least 2 sends"}
                        </Typography>
                    </Box>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>When</TableCell>
                                <TableCell>Kind</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell align="right">Result</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{formatWhen(row.createdAt)}</TableCell>
                                    <TableCell>{kindChip(row.kind)}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{row.title}</Typography>
                                        {row.body && (
                                            <Typography variant="caption" color="text.secondary">
                                                {row.body}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">{formatUserResult(row)}</Typography>
                                        {formatDeviceResult(row) ? (
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDeviceResult(row)}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        {`No ${name} notifications with sent > 0 yet.`}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
}

export default function NotificationOverviewPanel() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [overview, setOverview] = useState(null);
    const [appTab, setAppTab] = useState(() => {
        try {
            return window.localStorage.getItem(OVERVIEW_APP_TAB_KEY) || "";
        } catch {
            return "";
        }
    });

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        const result = await AdminNotificationService.getOverview();
        setLoading(false);
        if (!result.success) {
            setError(result.error || "Failed to load overview");
            return;
        }
        setOverview(result.data || null);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const apps = Array.isArray(overview?.apps) ? overview.apps : [];
    const activeCode = apps.some((app) => app.appCode === appTab)
        ? appTab
        : (apps[0]?.appCode || "");
    const selectedApp = apps.find((app) => app.appCode === activeCode) || null;

    const handleAppTabChange = (_, value) => {
        setAppTab(value);
        try {
            window.localStorage.setItem(OVERVIEW_APP_TAB_KEY, String(value));
        } catch {
            /* ignore quota / private mode */
        }
    };
    const batchSummary = apps.length > 0
        ? apps.map((app) => `${app.appName || app.appCode} every ${app.batchSize ?? "—"}`).join("; ")
        : "Web every 30; Jigsaw Muse every 30; Word Wheel Quest every 10; Sliding Puzzle Quest every 50";

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary">
                    Automatic publish pushes: {batchSummary}. {overview?.sendWindowLabel || "Mon–Fri 9:00–17:00 America/New_York"}.
                    Counts are stored in system settings and survive restarts.
                    {overview?.withinWorkingHours
                        ? " Currently inside the send window."
                        : overview?.nextWindowLabel
                          ? ` Next window: ${overview.nextWindowLabel}.`
                          : ""}
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshOutlinedIcon />}
                    onClick={load}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {overview?.pushEnabled === false && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Push delivery is disabled on the server. Counts still update, but nothing is sent.
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {loading && !overview && <LinearProgress sx={{ mb: 2 }} />}

            {apps.length > 0 && (
                <Tabs
                    value={activeCode}
                    onChange={handleAppTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                >
                    {apps.map((app) => (
                        <Tab
                            key={app.appCode}
                            value={app.appCode}
                            label={app.appName || app.appCode}
                        />
                    ))}
                </Tabs>
            )}

            {selectedApp && (
                <Grid container spacing={2} direction="column">
                    <Grid size={12}>
                        <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
                            <Grid size={{ xs: 12, md: selectedApp.dailyGift ? 6 : 12 }}>
                                <AppBatchCard
                                    app={selectedApp}
                                    onSendEnabledChange={async (appCode, enabled) => {
                                        const result = await AdminNotificationService.setSendEnabled(appCode, enabled);
                                        if (!result.success) {
                                            setError(result.error || "Failed to update send switch");
                                            return;
                                        }
                                        await load();
                                    }}
                                    onBatchSizeChange={async (appCode, batchSize) => {
                                        const result = await AdminNotificationService.setBatchSize(appCode, batchSize);
                                        if (!result.success) {
                                            setError(result.error || "Failed to update batch limit");
                                            return false;
                                        }
                                        await load();
                                        return true;
                                    }}
                                    onMemoChange={async (appCode, memo) => {
                                        const result = await AdminNotificationService.setMemo(appCode, memo);
                                        if (!result.success) {
                                            setError(result.error || "Failed to save memo");
                                            return false;
                                        }
                                        await load();
                                        return true;
                                    }}
                                />
                            </Grid>
                            {selectedApp.dailyGift && (
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <DailyGiftCard
                                        gift={selectedApp.dailyGift}
                                        onEnabledChange={async (enabled) => {
                                            const result = await AdminNotificationService.setDailyGiftEnabled(enabled);
                                            if (!result.success) {
                                                setError(result.error || "Failed to update daily gift");
                                                return;
                                            }
                                            await load();
                                        }}
                                        onTestOnlyChange={async (testOnly) => {
                                            const result = await AdminNotificationService.setDailyGiftTestOnly(testOnly);
                                            if (!result.success) {
                                                setError(result.error || "Failed to update test-account option");
                                                return;
                                            }
                                            await load();
                                        }}
                                        onSendTest={async () => {
                                            const result = await AdminNotificationService.sendDailyGiftTest();
                                            if (!result.success) {
                                                setError(result.error || "Failed to send test notification");
                                                return "";
                                            }
                                            const count = Number(result.data?.sentCount) || 0;
                                            const title = result.data?.title ? ` “${result.data.title}”` : "";
                                            await load();
                                            if (count === 0) {
                                                return "No test-account devices are registered for Word Wheel Quest.";
                                            }
                                            return `Sent${title} to ${count} test-account device${count === 1 ? "" : "s"}.`;
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid size={12}>
                        <RecentSendsCard
                            app={selectedApp}
                            rows={Array.isArray(selectedApp.recent) ? selectedApp.recent : []}
                            loading={loading}
                        />
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
